// Types
import Handsontable from 'handsontable/base';
import {
    applyHotTableTextStyle,
    CellDataChangeEntry,
    CellDataUpdateFunction,
    setHotTableCellData,
} from '../utils/hotInstanceUtils/hotTableUpdateUtils';
import { updateTableElement } from '../../tableActions';
import { getHotColWidthsGu } from '../utils/hotInstanceUtils/hotTableGridDimensionsUtils';
import getGridSize from '../../../../utils/grid/gridSizeSelector';
import { Thunk } from '../../../../types/reduxTypes';
import { CellData, CellTypeObject } from '../../../../../common/table/TableTypes';
import { CellTypeNames, TextAlignment, VerticalAlignment } from '../../../../../common/table/CellTypeConstants';
import { TABLE_ELEMENT_CELL_SELECTIONS_UPDATE } from '../../../../../common/elements/selectionConstants';
import { updateCellType as updateCellTypeUtil } from '../utils/tableCellTypeUtils';
import { userCurrencyPreferenceSelector } from '../../../../user/currentUserSelector';
import { getAllCellsBetween } from '../utils/tableCellSelectionUtils';

/**
 * Updates a table's data based on the provided cell data changes.
 *
 * NOTE: This performs an update via the Handsontable instance (and manual rendering),
 *  then dispatches the updated data to the store, and finally sets the data on the
 *  Handsontable instance again.
 * TODO - Investigate whether we can avoid the double update / ordering dependencies.
 */
export const updateEditedTableData =
    ({
        hotInstance,
        elementId,
        cellDataChanges,
        tableUpdateSource,
        transactionId,
    }: {
        hotInstance: Handsontable | null | undefined;
        elementId: string;
        cellDataChanges: CellDataChangeEntry[];
        transactionId?: number;
        tableUpdateSource: string;
    }): Thunk =>
    (dispatch, getState) => {
        const state = getState();
        const gridSize = getGridSize(state);

        const hotColWidthsGU = getHotColWidthsGu(hotInstance, gridSize);

        const undoRedoCellSelectionAction = {
            type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
            id: elementId,
            cellSelections: hotInstance!.getSelected(),
        };

        setHotTableCellData(
            hotInstance,
            cellDataChanges,
            tableUpdateSource,
            // Callback after the table render has occurred
            (newData: CellData[][]) => {
                dispatch(
                    updateTableElement({
                        id: elementId,
                        changes: {
                            tableContent: {
                                colWidthsGU: hotColWidthsGU,
                                data: newData,
                            },
                        },

                        // On undo/redo, make sure to go to previous cell selections
                        batchUndoActions: [undoRedoCellSelectionAction],
                        batchRedoActions: [undoRedoCellSelectionAction],

                        transactionId,
                        silent: false,
                    }),
                );
            },
        );
    };

/**
 * Performs a table data update when the instance of a Handsontable is provided
 * and dispatches the update to the store.
 */
const updateEditedTableDataForSelection = ({
    hotInstance,
    elementId,
    transactionId,
    cellDataUpdateFn,
    tableUpdateSource,
}: {
    hotInstance: Handsontable | null | undefined;
    elementId: string;
    transactionId?: number;
    cellDataUpdateFn: CellDataUpdateFunction;
    tableUpdateSource: string;
}) => {
    const cellSelections = hotInstance?.getSelected();
    const cellDataChanges = getAllCellsBetween(cellSelections).map<[number, number, (cellData: CellData) => CellData]>(
        (cell) => {
            const { row, col } = cell;
            return [row, col, cellDataUpdateFn];
        },
    );

    return updateEditedTableData({
        hotInstance,
        elementId,
        cellDataChanges,
        tableUpdateSource,
        transactionId,
    });
};

/**
 * Updates the text alignment for all selected cells in the table.
 */
export const updateTableCellTextAlignment = ({
    hotInstance,
    elementId,
    transactionId,
    textAlignment,
}: {
    hotInstance: Handsontable | null | undefined;
    textAlignment: TextAlignment;
    elementId: string;
    transactionId?: number;
}) =>
    updateEditedTableDataForSelection({
        hotInstance,
        elementId,
        transactionId,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            textAlignment,
        }),
        tableUpdateSource: 'CellMeta.updateCellTextAlignment',
    });

/**
 * Updates the vertical alignment for all selected cells in the table.
 */
export const updateTableCellVerticalAlignment = ({
    hotInstance,
    elementId,
    transactionId,
    verticalAlignment,
}: {
    hotInstance: Handsontable | null | undefined;
    verticalAlignment: VerticalAlignment;
    elementId: string;
    transactionId?: number;
}) =>
    updateEditedTableDataForSelection({
        hotInstance,
        elementId,
        transactionId,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            verticalAlignment,
        }),
        tableUpdateSource: 'CellMeta.updateCellVerticalAlignment',
    });

/**
 * Updates the background colour for all selected cells in the table.
 */
export const updateTableCellBackgroundColor = ({
    hotInstance,
    elementId,
    backgroundColor,
    transactionId,
}: {
    hotInstance: Handsontable | null | undefined;
    elementId: string;
    backgroundColor: string | null;
    transactionId?: number;
}) =>
    updateEditedTableDataForSelection({
        hotInstance,
        elementId,
        transactionId,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            background: backgroundColor,
        }),
        tableUpdateSource: 'CellMeta.updateCellBackground',
    });

/**
 * Updates the cell type for all the selected cells in the table.
 */
export const updateTableCellType =
    ({
        hotInstance,
        elementId,
        cellType,
        transactionId,
    }: {
        hotInstance: Handsontable | null | undefined;
        elementId: string;
        cellType: CellTypeNames | CellTypeObject;
        transactionId?: number;
    }): Thunk =>
    (dispatch, getState) => {
        const state = getState();
        const currencyPreference = userCurrencyPreferenceSelector(state);

        if (typeof cellType === 'string') {
            return dispatch(
                updateEditedTableDataForSelection({
                    hotInstance,
                    elementId,
                    transactionId,
                    cellDataUpdateFn: (cellData: CellData, row?: number, col?: number) =>
                        updateCellTypeUtil(cellData, cellType, currencyPreference, elementId, row, col, hotInstance),
                    tableUpdateSource: 'CellMeta.updateCellType',
                }),
            );
        }

        return dispatch(
            updateEditedTableDataForSelection({
                hotInstance,
                elementId,
                transactionId,
                cellDataUpdateFn: (cellData: CellData) => ({
                    ...cellData,
                    type: cellType,
                }),
                tableUpdateSource: 'CellMeta.updateCellType',
            }),
        );
    };

/**
 * Updates the cell type format for all the selected cells in the table.
 */
export const updateTableCellTypeFormat = ({
    hotInstance,
    elementId,
    typeFormatChanges,
    transactionId,
}: {
    hotInstance: Handsontable | null | undefined;
    elementId: string;
    typeFormatChanges: CellTypeObject;
    transactionId?: number;
}) =>
    updateEditedTableDataForSelection({
        hotInstance,
        elementId,
        transactionId,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            type: { ...cellData.type, ...typeFormatChanges },
        }),
        tableUpdateSource: 'CellMeta.updateCellTypeFormat',
    });

/**
 * Applies/removes a text style to all selected cells.
 */
export const updateTableTextStyle =
    ({
        hotInstance,
        elementId,
        textStyle,
    }: {
        hotInstance: Handsontable | null | undefined;
        elementId: string;
        textStyle: string | null;
    }): Thunk =>
    (dispatch, getState) => {
        const newData = applyHotTableTextStyle(hotInstance, textStyle);
        if (!newData) return;

        const state = getState();
        const gridSize = getGridSize(state);

        const hotColWidthsGU = getHotColWidthsGu(hotInstance, gridSize);

        const undoRedoCellSelectionAction = {
            type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
            id: elementId,
            cellSelections: hotInstance!.getSelected(),
        };

        dispatch(
            updateTableElement({
                id: elementId,
                changes: {
                    tableContent: {
                        colWidthsGU: hotColWidthsGU,
                        data: newData,
                    },
                },

                // On undo/redo, make sure to go to previous cell selections
                batchUndoActions: [undoRedoCellSelectionAction],
                batchRedoActions: [undoRedoCellSelectionAction],
                silent: false,
                transactionId: undefined,
            }),
        );
    };
